import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { companyResourcesPath, companySignaturesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Props {
  signature: {
    name: string
    body: string
  }
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Signatures", href: companySignaturesPath() },
  { title: "Add", href: "/company/signatures/new" },
]

const MERGE_FIELDS = [
  "{{empName}}",
  "{{empTitle}}",
  "{{empEmail}}",
  "{{empCell}}",
  "{{empProfile}}",
  "{{empVCard}}",
]

export default function New({ signature, errors }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    name: signature.name,
    body: signature.body,
  })

  transform((data) => ({ signature: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(companySignaturesPath())
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Add Signature" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Signature</h1>

        <form onSubmit={handleSubmit} className="max-w-xl">
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="body" className="mb-1 block font-medium">
              Text
            </label>
            <textarea
              id="body"
              value={data.body}
              onChange={(e) => setData("body", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={10}
            />
            {errors?.body && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.body.join(", ")}
              </p>
            )}
          </div>

          <div className="bg-hpi-pale-blue mb-4 rounded p-3 text-sm">
            <span className="font-medium">Merge Fields:</span>{" "}
            {MERGE_FIELDS.join("  ")}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add
            </button>
            <Link
              href={companySignaturesPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
